import { Entity, SystemInterface } from '@goodboydigital/odie';

import { Settings } from '../../AppConfig';
import { ReplayEntry } from '../../plugins/APIPlugin';
import { RecFrame } from '../../ui/RecFrame';
import { RecStatus } from '../../ui/RecStatus';
import { Browser } from '../../utils/Browser';
import { Maths } from '../../utils/Maths';
import { Random } from '../../utils/Random';
import { VehicleData } from '../entities/VehicleEntity';
import { Game } from '../Game';

const pako = require('pako');

export interface ReplayData extends VehicleData
{
    /** Player's nickname. Ex.: ABC */
    nickname: string,
    /** Level system version where the replay was recorded */
    systemVersion: number,
    /** Name of the level */
    levelName: string,
    /** Random seed of the level */
    levelSeed: string,
    /** Unique hash of the level */
    levelHash: string,
    /** Length of the replay, in miliseconds */
    time: number,
    /** How many frames each step represents - 8 means that the record was made each 8 frames */
    stepSize: number,
    /** Buffer of state - one value per index */
    stateBuffer: number[],
    /** Transform buffer of the character - a[0] = X, a[1] = Y, a[2] = R */
    characterBuffer: number[],
    /** Transform buffer of the chassis - a[0] = X, a[1] = Y, a[2] = R */
    chassisBuffer: number[],
    /** Transform buffer of the left wheel - a[0] = X, a[1] = Y, a[2] = R */
    wheelLeftBuffer: number[],
    /** Transform buffer of the right wheel - a[0] = X, a[1] = Y, a[2] = R */
    wheelRightBuffer: number[],

}

/** Define how many update ticks each record item will repesent */
const REPLAY_STEP_SIZE = 8;

/** How many seconds should be recorded after player crosses end flag  */
const REPLAY_TIME_AFTER_FINISH = 3;

/** Max time for a replay, in miliseconds. Recording will stop after that  */
const REPLAY_MAX_TIME = Math.round(1000 * 60 * 1.5);

/** If true, the system will upload recorded runs only if best time has been beaten  */
const REPLAY_UPLOAD_ONLY_BEST_RUNS = false;

export class ReplaySystem implements SystemInterface
{
    public game: Game;

    /** Rec mode, to record user run and upload data to firebase */
    public rec: boolean;

    /** Tells if the system is recording or not */
    public recording: boolean;

    /** Use built-in replays or not - should be removed at some stage */
    public useLocalReplays: boolean;

    /** List of replay data that should be set up in a race */
    public replayDataList: ReplayData[];

    private recFrame?: RecFrame;
    private recStatus?: RecStatus;

    constructor(entity: Entity)
    {
        this.game = entity.scene as Game;
        this.replayDataList = [];
        this.game.onIdle.connect(this.gameIdle.bind(this));
        this.game.onPlay.connect(this.gamePlay.bind(this));
        this.game.race.onRaceCountdownStart.connect(this.raceCountdownStart.bind(this));
        this.game.race.onRaceStart.connect(this.raceStart.bind(this));
        this.game.race.onRaceFinish.connect(this.raceFinish.bind(this));

        this.useLocalReplays = false;
        this.recording = false;
        this.rec = Settings.rec;
    }

    public update(): void
    {
        if (!this.rec) return;

        // Cancel recording if user has been playing for too long
        if (this.game.race.running && this.game.race.time >= REPLAY_MAX_TIME && this.recording)
        {
            this.stopRecording();
        }
    }

    private gameIdle(): void
    {
        this.stopRecording();
        this.stopPlayback();
    }

    private gamePlay(): void
    {
        if (this.rec)
        {
            this.game.garage.randomiseVehicle(true, this.game.race.getCurrentRaceGearTier());

            if (!this.recStatus) this.recStatus = new RecStatus();
        }

        this.setupReplays(this.game.race.getCurrentRaceMaxOpponents());
    }

    private raceCountdownStart(): void
    {
        // Current level hash
        const levelHash = this.game.level.getLevelHash();

        // If we have less data than the max number players, complete with built-in data
        // That should prevent empty races, as built-in data is loaded within game assets
        if (this.replayDataList.length < this.game.race.getCurrentRaceMaxOpponents())
        {
            const localDataList = this.game.app.api.fetchLocalReplays(levelHash, 5);

            this.replayDataList.push(...localDataList);
        }

        // Just to ensure that there are no repeated replays
        this.replayDataList = this.filterUniqueReplayData(this.replayDataList);

        // Actually convert replay data into ghosts for the race
        for (const data of this.replayDataList)
        {
            this.setupGhost(data, levelHash);
        }

        // Clean up data list as all data was already converted to ghosts
        this.replayDataList = [];
    }

    private raceStart(): void
    {
        this.stopPlayback();
        this.startPlayback();
        this.startRecording();
    }

    private raceFinish(): void
    {
        this.game.physics.timer.dispatchAfter(REPLAY_TIME_AFTER_FINISH, () =>
        {
            if (this.recording) this.uploadReplay();
            this.stopRecording();
        });
    }

    private startPlayback(): void
    {
        for (const ghost of this.game.race.ghosts) ghost.replayStartPlayback();
    }

    private stopPlayback(): void
    {
        for (const ghost of this.game.race.ghosts) ghost.replayStopPlayback();
    }

    private startRecording(): void
    {
        if (this.recording || !this.rec || this.game.race.raceIndex <= 0) return;
        this.recording = true;
        this.game.vehicle.replayStartRecording();
        console.log('[ReplaySystem] Start recording');

        if (!this.recFrame) this.recFrame = new RecFrame();
    }

    private stopRecording(): void
    {
        if (!this.recording) return;
        this.recording = false;
        this.game.vehicle.replayStopRecording();
        console.log('[ReplaySystem] Stop recording');
    }

    private async setupReplays(maxPlayers = 5): Promise<void>
    {
        this.replayDataList = [];
        const levelHash = this.game.level.getLevelHash();
        const entries = await this.game.app.api.fetchLevelReplayEntries(levelHash);
        const selected = this.getOpponents(entries, maxPlayers);

        console.log(`[ReplaySystem] Opponents available for this race:`, entries.length);
        console.log(`[ReplaySystem] Opponents selected for this race:`, selected.length);

        for (const entry of selected)
        {
            this.game.app.api.fetchLevelReplayFile(entry.replayHash).then((data) =>
            {
                // Cancel if loading finished after race is not idle anymore, or if data is invalid
                if (!this.game.race.idle || !data) return;

                console.log(`[ReplaySystem] Opponent file downloaded:`, data);

                // Pool loaded data in the replayDataList, that will be
                // added as 'ghosts' once race countdown starts
                this.replayDataList.push(data);
            });
        }
    }

    private getOpponents(entries: ReplayEntry[], maxPlayers: number): ReplayEntry[]
    {
        switch (this.game.race.getCurrentRaceDifficulty())
        {
            case 'easy':
                return this.getEasyOpponents(entries, maxPlayers);
            case 'normal':
                return this.getNormalOpponents(entries, maxPlayers);
            case 'hard':
                return this.getHardOpponents(entries, maxPlayers);
            default:
                return this.getRandomOpponents(entries, maxPlayers);
        }
    }

    private getEasyOpponents(entries: ReplayEntry[], maxPlayers: number): ReplayEntry[]
    {
        // Clone entries
        let result = entries.slice(0);

        // Order entries by descending replayTime - slower ones first
        result = result.sort((a, b) => b.replayTime - a.replayTime);

        // Cap result by max players
        if (result.length > maxPlayers) result.length = maxPlayers;

        return result;
    }

    private getNormalOpponents(entries: ReplayEntry[], maxPlayers: number): ReplayEntry[]
    {
        // Clone entries
        let result = entries.slice(0);

        // Order entries by ascending replayTime - faster ones first
        result = result.sort((a, b) => a.replayTime - b.replayTime);

        const diff = result.length - Math.min(result.length, maxPlayers);
        const offset = Math.floor(diff / 2);

        // Get entries from the middle of given array
        result = result.slice(offset, offset + maxPlayers);

        return result;
    }

    private getHardOpponents(entries: ReplayEntry[], maxPlayers: number): ReplayEntry[]
    {
        // Clone entries
        let result = entries.slice(0);

        // Order entries by ascending replayTime - faster ones first
        result = result.sort((a, b) => a.replayTime - b.replayTime);

        console.log(result.slice(0));

        // Cap result by max players
        if (result.length > maxPlayers) result.length = maxPlayers;

        return result;
    }

    private getRandomOpponents(entries: ReplayEntry[], maxPlayers: number): ReplayEntry[]
    {
        // Shuffle opponents
        const result = Random.shuffle(entries.slice(0));

        // Cap result by max players
        if (result.length > maxPlayers) result.length = maxPlayers;

        return result;
    }

    private setupGhost(replay: ReplayData, levelHash: string): void
    {
        if (!this.game.playing) return;
        const currentLevelHash = this.game.level.getLevelHash();

        // Extra safety measure to prevent replays from wrong levels
        if (levelHash !== currentLevelHash) return;

        // Attempt to add a ghost vehicle for this replay data. Might fail if there are enough players already;
        // or if level hash does not match; or if replay hash is repeated
        const vehicle = this.game.race.addGhost(replay);

        // A ghost might not be added if is repeated character or something else
        if (!vehicle) return;

        try
        {
            vehicle.replay.setBuffer(replay.stateBuffer, replay.stepSize || 1);
            vehicle.character.replay.setBuffer(replay.characterBuffer, replay.stepSize || 1);
            vehicle.chassis.replay.setBuffer(replay.chassisBuffer, replay.stepSize || 1);
            vehicle.wheelLeft.replay.setBuffer(replay.wheelLeftBuffer, replay.stepSize || 1);
            vehicle.wheelRight.replay.setBuffer(replay.wheelRightBuffer, replay.stepSize || 1);

            vehicle.character.replay.playbackOffsetX = this.game.level.courseStartX - vehicle.chassis.replay.buffer[0];
            vehicle.chassis.replay.playbackOffsetX = this.game.level.courseStartX - vehicle.chassis.replay.buffer[0];
            vehicle.wheelLeft.replay.playbackOffsetX = this.game.level.courseStartX - vehicle.chassis.replay.buffer[0];
            vehicle.wheelRight.replay.playbackOffsetX = this.game.level.courseStartX - vehicle.chassis.replay.buffer[0];

            vehicle.replay.reset();
            vehicle.character.replay.reset();
            vehicle.chassis.replay.reset();
            vehicle.wheelLeft.replay.reset();
            vehicle.wheelRight.replay.reset();

            vehicle.character.replay.playbackStep(0);
            vehicle.chassis.replay.playbackStep(0);
            vehicle.wheelLeft.replay.playbackStep(0);
            vehicle.wheelRight.replay.playbackStep(0);

            vehicle.character.replay.hide();
            vehicle.chassis.replay.hide();
            vehicle.wheelLeft.replay.hide();
            vehicle.wheelRight.replay.hide();

            const delay = Settings.fastplay ? 0.1 : Random.range(0.1, 1.4);

            vehicle.character.replay.dropFromTheSky(delay);
            vehicle.chassis.replay.dropFromTheSky(delay);
            vehicle.wheelLeft.replay.dropFromTheSky(delay);
            vehicle.wheelRight.replay.dropFromTheSky(delay);
        }
        catch (e)
        {
            console.warn(e);
            console.log('Invalid replay data:', replay);
        }
    }

    private getReplayData(): ReplayData
    {
        const stepSize = REPLAY_STEP_SIZE;
        const data: ReplayData = {
            ...this.game.app.user.data.vehicle,
            systemVersion: this.game.level.LEVEL_SYSTEM_VERSION,
            levelName: this.game.level.levelName,
            levelSeed: '', // Not being used anymore
            levelHash: this.game.level.getLevelHash(),
            time: this.game.race.finishTime,
            stepSize,
            stateBuffer: this.optimiseBuffer(this.game.vehicle.replay.buffer, 1, stepSize),
            characterBuffer: this.optimiseBuffer(this.game.vehicle.character.replay.buffer, 3, stepSize),
            chassisBuffer: this.optimiseBuffer(this.game.vehicle.chassis.replay.buffer, 3, stepSize),
            wheelLeftBuffer: this.optimiseBuffer(this.game.vehicle.wheelLeft.replay.buffer, 3, stepSize),
            wheelRightBuffer: this.optimiseBuffer(this.game.vehicle.wheelRight.replay.buffer, 3, stepSize),
        };

        return data;
    }

    private async uploadReplay(): Promise<void>
    {
        if (!this.recording) return;
        if (!this.game.vehicle.character.replay.buffer.length) return;
        if (REPLAY_UPLOAD_ONLY_BEST_RUNS && !this.game.race.bestRunBeaten) return;
        
        if(window.confirm("Submit Replay to Firebase?"))
        {
            const data = await this.getReplayData();
            const json = JSON.stringify(data);
            const replayHash = await Browser.sha256(json);

            console.log('[ReplaySystem] Uploading replay');
            
            this.game.app.api.submitReplay({
                systemVersion: data.systemVersion,
                levelHash: data.levelHash,
                replayHash,
                replayTime: data.time,
            }, this.zipReplay(data));
        }
        else
        {
            console.log('[ReplaySystem] Not uploading replay');
        }

    }

    private optimiseBuffer(buffer: number[], itemSize: number, stepSize: number): number[]
    {
        const result: number[] = [];

        for (let i = 0; i < buffer.length; i++)
        {
            const step = Math.floor(i / itemSize);

            // Skip steps based on step size
            if (step % stepSize !== 0) continue;

            result.push(Maths.toFixed(buffer[i], 2));
        }

        return result;
    }

    protected zipReplay(data: ReplayData): Uint8Array
    {
        const json = JSON.stringify(data);

        return pako.deflate(json);
    }

    protected unzipReplay(buffer: ArrayBuffer): ReplayData
    {
        const json = pako.inflate(buffer, { to: 'string' });

        return JSON.parse(json);
    }

    private filterUniqueReplayData(dataList: ReplayData[]): ReplayData[]
    {
        const flags = {} as Record<string, boolean>;
        const output = [] as ReplayData[];
        const l = dataList.length;

        for (let i = 0; i < l; i++)
        {
            const data = dataList[i];

            if (flags[data.time]) continue;
            flags[data.time] = true;
            output.push(data);
        }

        return output;
    }
}
